/*
 * Copyright 2026 Sony Corporation
 */

/**
 * The stand-in person coverage is measured on (camera-simulation.md §12).
 *
 * Built from capsules, and only capsules, because a capsule is both a mesh
 * Babylon can draw and a shape a ray can be tested against in closed form: the
 * picture and the occlusion maths are the same body by construction. Arms in an
 * A-pose, which is how a volumetric capture subject stands and what leaves the
 * armpits and the inside of the legs visible at all.
 */

export type Point = readonly [number, number, number];

export interface Part {
  name: string;
  /** Segment ends, in metres, feet at the origin, facing +Z. A sphere has `a === b`. */
  a: Point;
  b: Point;
  radius: number;
}

/** Proportions of a 1.75 m adult; every part scales with the height asked for. */
export const REFERENCE_HEIGHT_M = 1.75;

const both = (name: string, a: Point, b: Point, radius: number): Part[] => [
  { name: `left ${name}`, a: [-a[0], a[1], a[2]], b: [-b[0], b[1], b[2]], radius },
  { name: `right ${name}`, a, b, radius },
];

const REFERENCE_PARTS: readonly Part[] = [
  { name: 'head', a: [0, 1.6, 0.01], b: [0, 1.66, 0.01], radius: 0.1 },
  { name: 'neck', a: [0, 1.44, 0], b: [0, 1.53, 0], radius: 0.055 },
  { name: 'chest', a: [-0.07, 1.3, 0], b: [0.07, 1.3, 0], radius: 0.14 },
  { name: 'waist', a: [0, 1.02, 0], b: [0, 1.18, 0], radius: 0.13 },
  { name: 'hips', a: [-0.06, 0.92, 0], b: [0.06, 0.92, 0], radius: 0.13 },
  ...both('upper arm', [0.21, 1.37, 0], [0.3, 1.1, 0], 0.045),
  ...both('forearm', [0.3, 1.1, 0], [0.37, 0.86, 0.02], 0.038),
  ...both('hand', [0.38, 0.8, 0.02], [0.39, 0.74, 0.02], 0.04),
  ...both('thigh', [0.1, 0.84, 0], [0.11, 0.48, 0], 0.075),
  ...both('shin', [0.11, 0.48, 0], [0.11, 0.1, 0], 0.052),
  ...both('foot', [0.11, 0.04, -0.03], [0.11, 0.04, 0.13], 0.04),
];

export interface SubjectPlacement {
  /** Where the feet stand, in world metres. */
  position: Point;
  height: number;
  /** Heading in radians about +Y; zero faces +Z. */
  heading: number;
}

/** The parts in world space, for a subject standing at `placement`. */
export const subjectParts = ({ position, height, heading }: SubjectPlacement): Part[] => {
  const scale = height / REFERENCE_HEIGHT_M;
  const cos = Math.cos(heading);
  const sin = Math.sin(heading);
  const place = ([x, y, z]: Point): Point => [
    position[0] + (x * cos + z * sin) * scale,
    position[1] + y * scale,
    position[2] + (-x * sin + z * cos) * scale,
  ];
  return REFERENCE_PARTS.map((part) => ({
    name: part.name,
    a: place(part.a),
    b: place(part.b),
    radius: part.radius * scale,
  }));
};
